import { Button, Checkbox, FormControlLabel, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { ArrowLeft } from 'react-feather';
import { Link as RouterLink } from 'react-router-dom';
import { ACTIVITY_MODES, ACTIVITY_PERIODS, ActivityMode, ActivityPeriod, MODE_LABELS, PERIOD_LABELS } from './activityWindow';

type ActivityControlsProps = {
  period: ActivityPeriod;
  mode: ActivityMode;
  caption?: string;
  /** Where the level above this one lives. Absent at the top level. */
  upTo?: string;
  /** Trailing-average lines on the two charts that carry them. */
  showTrend: boolean;
  onPeriod: (period: ActivityPeriod) => void;
  onMode: (mode: ActivityMode) => void;
  onShowTrend: (showTrend: boolean) => void;
};

/**
 * The page's two independent controls: how long a window to look at, and
 * whether it is a rolling one or runs from the start of the current calendar
 * period. Six combinations, all meaningful.
 *
 * They sit above the cards rather than inside one because they drive every card
 * on the page, the KPI row included.
 */
export const ActivityControls = ({ period, mode, caption, upTo, showTrend, onPeriod, onMode, onShowTrend }: ActivityControlsProps): ReactElement => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'stretch', sm: 'center' }}
    justifyContent="flex-end"
    spacing={{ xs: 1, sm: 2 }}
    sx={{ mb: 3 }}
  >
    {/* The window this whole page is about, so it carries more weight than a
        caption: a third larger than the small text, in primary ink. Below the
        top level an arrow sits to its left, always in the same place, so the
        way back up never moves. */}
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
      {upTo && (
        <Button component={RouterLink} to={upTo} size="small" sx={{ minWidth: 0, p: 0.5 }} aria-label="up one level">
          <ArrowLeft size={18} />
        </Button>
      )}
      {caption && (
        <Typography variant="caption" color="text.primary" sx={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
          {caption}
        </Typography>
      )}
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {/* The trend belongs with the window controls rather than in one card's
          corner: it turns the line on in every chart that has one. */}
      <FormControlLabel
        control={<Checkbox size="small" checked={showTrend} onChange={(event) => onShowTrend(event.target.checked)} />}
        label={
          <Typography variant="caption" color="text.secondary">
            Trend
          </Typography>
        }
        sx={{ mr: 0.5 }}
      />
      <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, value) => value && onPeriod(value as ActivityPeriod)} aria-label="period">
        {ACTIVITY_PERIODS.map((value) => (
          <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 1.5 }}>
            {PERIOD_LABELS[value]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, value) => value && onMode(value as ActivityMode)} aria-label="mode">
        {ACTIVITY_MODES.map((value) => (
          <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 1.5 }}>
            {MODE_LABELS[value]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  </Stack>
);

export default ActivityControls;
